import {
  Blocks,
  Component,
  FileBarChart,
  Sheet,
  ShieldCheck,
  SquareDashedKanban,
  Users,
  Workflow,
} from 'lucide-react'
import { useLocation, matchPath } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { MenuDrawerButton } from '@components/layout/pageWithNav'
import { MainNav } from '@components/mainNavigation'
import { useApprovalsEnabled } from '@global/hooks/useApprovalsEnabled'
import { useAppSelector } from '@redux/hooks'
import TaxonomyIcon from 'images/taxonomy_icon.svg'
import { URLS, menuProps, hasAnyPermission, hasPermission } from './utils'
import { useObserveAriaExpandedNav } from '../pageWithNav/useObserveAriaExpandedNav'
import { t } from '@lingui/core/macro'

export const Navigation = ({ toggleNavExpansion, getMenuExpandedStatus }) => {
  const account = useAppSelector(state => state.Auth.account)
  const permissions = useAppSelector(state => state.Auth.user?.permissions)
  const isAdmin = permissions?.includes('all_privileges')
  const [
    DATA_STANDARDS_DASHBOARD = false,
    TAXONOMY_TRANSLATION_MAPPING = false,
    USER_ACTIVITY_LOG = false,
  ] = useAppSelector(
    selectFeatureIncludes([
      FeatureDict.DATA_STANDARDS_DASHBOARD,
      FeatureDict.TAXONOMY_TRANSLATION_MAPPING,
      FeatureDict.USER_ACTIVITY_LOG,
    ]),
  )
  const location = useLocation()
  const isApprovalsEnabled = useApprovalsEnabled()

  const isCurrentPath = (path: string) =>
    matchPath(location.pathname, {
      path,
      exact: true,
      strict: true,
    })
  const iconConfig = { strokeWidth: 1.5 }

  const ariaExpandedValue = useObserveAriaExpandedNav({ getMenuExpandedStatus })

  return (
    <MainNav>
      <MainNav.Header name={account.name} slug={account.logo_slug} />
      <MainNav.Menu>
        <MenuDrawerButton
          isMenuItem={true}
          onClick={toggleNavExpansion}
          getMenuExpandedStatus={getMenuExpandedStatus}
        />

        {DATA_STANDARDS_DASHBOARD && isAdmin ? (
          <MainNav.MenuItem
            text={t`Dashboard`}
            icon={<ShieldCheck {...iconConfig} />}
            {...menuProps(location, URLS.complianceDashboard, null)}
          />
        ) : null}

        <MainNav.MenuItem
          text={t`Datasets`}
          icon={<Sheet {...iconConfig} />}
          {...menuProps(location, URLS.datasets, null)}
        />

        <MainNav.NestedMenu
          text={t`Submissions`}
          ariaExpandedValue={ariaExpandedValue}
          icon={
            <Blocks
              {...iconConfig}
              color={isCurrentPath(URLS.submissions.root) ? '#5DD39E' : 'white'}
            />
          }
          {...menuProps(location, URLS.submissions, 'root', permissions)}
        >
          <MainNav.NestedMenuItem
            text={t`Pending`}
            {...menuProps(location, URLS.submissions, 'pending', permissions)}
          />
          {isApprovalsEnabled ? (
            <MainNav.NestedMenuItem
              text={t`Approval Requests`}
              {...menuProps(location, URLS.submissions, 'approvals', permissions)}
            />
          ) : null}
        </MainNav.NestedMenu>

        <MainNav.MenuItem
          text={t`Reports`}
          icon={<FileBarChart {...iconConfig} />}
          {...menuProps(location, URLS.reports, null)}
        />
      </MainNav.Menu>

      {hasAnyPermission(permissions, [
        'manage_users',
        'manage_groups',
        'manage_connectors',
        'manage_targets',
        'manage_field_sets',
        'manage_lists',
        'manage_templates',
        'manage_folders',
        'manage_patterns',
        'manage_account_settings',
        'access_reports',
      ]) ? (
        <MainNav.Menu>
          <MainNav.MenuTitle>{t`Admin settings`}</MainNav.MenuTitle>

          {hasPermission(permissions, 'all_privileges') && TAXONOMY_TRANSLATION_MAPPING ? (
            <MainNav.MenuItem
              text={t`Taxonomy Translation`}
              icon={<TaxonomyIcon height="24px" width="24px" viewBox="0 0 28 28" {...iconConfig} />}
              {...menuProps(location, URLS.taxonomyTranslation, null)}
            />
          ) : null}

          {hasPermission(permissions, 'access_reports') ||
          hasPermission(permissions, 'manage_account_settings') ||
          hasPermission(permissions, 'manage_groups') ||
          hasPermission(permissions, 'manage_users') ? (
            <MainNav.NestedMenu
              text={t`Organizations`}
              icon={<Users {...iconConfig} />}
              ariaExpandedValue={ariaExpandedValue}
              {...menuProps(location, URLS.admin.organizations, 'root', permissions)}
            >
              {hasPermission(permissions, 'access_reports') && (
                <MainNav.NestedMenuItem
                  text={t`Reports`}
                  {...menuProps(location, URLS.admin.organizations, 'reports', permissions)}
                />
              )}
              {hasPermission(permissions, 'manage_account_settings') && (
                <MainNav.NestedMenuItem
                  text={t`Settings`}
                  {...menuProps(location, URLS.admin.organizations, 'settings', permissions)}
                />
              )}
              {hasPermission(permissions, 'manage_users') && (
                <MainNav.NestedMenuItem
                  text={t`Users`}
                  {...menuProps(location, URLS.admin.organizations, 'users', permissions)}
                />
              )}
              {hasPermission(permissions, 'all_privileges') && USER_ACTIVITY_LOG && (
                <MainNav.NestedMenuItem
                  text={t`User Activity Log`}
                  {...menuProps(location, URLS.admin.organizations, 'activities', permissions)}
                />
              )}
              {hasPermission(permissions, 'manage_groups') && (
                <MainNav.NestedMenuItem
                  text={t`Groups`}
                  {...menuProps(location, URLS.admin.organizations, 'groups', permissions)}
                />
              )}
            </MainNav.NestedMenu>
          ) : null}

          {hasPermission(permissions, 'manage_connectors') ||
          hasPermission(permissions, 'manage_targets') ? (
            <MainNav.NestedMenu
              text={t`Integrations`}
              icon={<Workflow {...iconConfig} />}
              ariaExpandedValue={ariaExpandedValue}
              {...menuProps(location, URLS.admin.integrations, 'root', permissions)}
            >
              {hasPermission(permissions, 'manage_connectors') && (
                <MainNav.NestedMenuItem
                  text={t`Accounts`}
                  {...menuProps(location, URLS.admin.integrations, 'accounts', permissions)}
                />
              )}
              {hasPermission(permissions, 'manage_targets') && (
                <MainNav.NestedMenuItem
                  text={t`Targets`}
                  {...menuProps(location, URLS.admin.integrations, 'targets', permissions)}
                />
              )}
            </MainNav.NestedMenu>
          ) : null}

          {hasPermission(permissions, 'manage_patterns') ||
          hasPermission(permissions, 'manage_lists') ||
          hasPermission(permissions, 'manage_field_sets') ? (
            <MainNav.NestedMenu
              text={t`Elements`}
              icon={<Component {...iconConfig} />}
              ariaExpandedValue={ariaExpandedValue}
              {...menuProps(location, URLS.admin.elements, 'root', permissions)}
            >
              {hasPermission(permissions, 'manage_field_sets') && (
                <MainNav.NestedMenuItem
                  text={t`Field Sets`}
                  {...menuProps(location, URLS.admin.elements, 'fieldsets', permissions)}
                />
              )}
              {hasPermission(permissions, 'manage_patterns') && (
                <MainNav.NestedMenuItem
                  text={t`Patterns`}
                  {...menuProps(location, URLS.admin.elements, 'patterns', permissions)}
                />
              )}
              {hasPermission(permissions, 'manage_lists') && (
                <MainNav.NestedMenuItem
                  text={t`Pick Lists`}
                  {...menuProps(location, URLS.admin.elements, 'lists', permissions)}
                />
              )}
            </MainNav.NestedMenu>
          ) : null}

          {hasPermission(permissions, 'manage_templates') ||
          hasPermission(permissions, 'manage_folders') ? (
            <MainNav.NestedMenu
              {...menuProps(location, URLS.admin.templates, 'root', permissions)}
              ariaExpandedValue={ariaExpandedValue}
              text={t`Templates`}
              icon={
                <SquareDashedKanban
                  {...iconConfig}
                  color={isCurrentPath('/admin/templates') ? '#5DD39E' : 'white'}
                />
              }
            >
              {hasPermission(permissions, 'manage_folders') && (
                <MainNav.NestedMenuItem
                  text={t`Folders`}
                  {...menuProps(location, URLS.admin.templates, 'folders', permissions)}
                />
              )}
            </MainNav.NestedMenu>
          ) : null}
        </MainNav.Menu>
      ) : null}
    </MainNav>
  )
}
